/* Heavily inspired from material-ui ClickAwayListener.js */
import React, { useEffect, useRef } from 'react';
import EventListener from 'react-event-listener';

export interface ClickAwayListenerProps
  extends Omit<React.HTMLAttributes<any>, 'children'> {
  /**
   * Children prop, it should be a function to return a React node.
   */
  children: (
    ref: (c?: HTMLElement | null) => void | React.RefObject<HTMLElement>,
    props?: any
  ) => JSX.IntrinsicElements | React.ReactNode;
  /**
   * Callback on click away.
   */
  onClickAway?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

/**
 * Renders Click Away Listener
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const ClickAwayListener = ({
  onClickAway,
  children,
  ...childProps
}: ClickAwayListenerProps) => {
  const [refNode, setRefNode] = React.useState<
    HTMLElement | null | undefined
  >();
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Function to handle onClick
   *
   * - @param event MouseEvent
   */
  const handleClickAway = (event: React.MouseEvent | React.KeyboardEvent) => {
    // Ignore events if  `event.preventDefault()` marked.
    // Ignore if child render null.
    // Ignore if not mounted, for IE11 support, trigger the handleClickAway even after the unbind.
    if (event.defaultPrevented || !refNode || !isMounted.current) {
      return;
    }

    const doc = refNode.ownerDocument;
    const target = event.target as HTMLElement;

    if (
      doc &&
      doc.documentElement &&
      doc.documentElement.contains(target) &&
      !refNode.contains(target)
    ) {
      onClickAway && onClickAway(event);
    }
  };

  return (
    <EventListener target="document" onMouseUp={handleClickAway}>
      {children(setRefNode, childProps)}
    </EventListener>
  );
};

export default ClickAwayListener;
